import { FC, useEffect, useRef, useState } from 'react';
import Button from '../../../Button';
import PaymentProcessingTable from './PaymentProcessingTable';
import { useTranslation } from 'react-i18next';
import {
  IGetOrderInfo,
  IOrderInfo,
  IPaymentAmountTableResult,
  IPaymentResult,
  IStudentPayment,
  IUpdatePaymentBody,
} from '../../../../models/payments.model';
import { IUpdatePaymentLastBody } from '../../../../models/nicePayments.model';
import { EPaymentMode, EPaymentStep } from '../StudentPaymentPane';
import styled from 'styled-components';
import { IPaymentAmount, ISelectedCoupon } from '../../../../models/studentStudent.model';
import { IParent, IStudent } from '../../../../models/students.model';
import useNotification from '../../../../hooks/useNotification';
import { getTossInstallment } from '../../../../lib/toss';
import useAxios from '../../../../hooks/useAxios';
import useLoader from '../../../../hooks/useLoader';
import {
  adminPaymentTodo,
  createNicePaymentTodo,
  postNicePaymentLog,
  updatePaymentTodo,
  getPaymentStatus,
  updatePaymentStatus,
} from '../../../../api/nicePayments.api';
import useToss from '../../../../hooks/useToss';
import usePayment from '../../../../hooks/usePayment';
import { IGetStudentCoupons, IGetStudentCouponTypes } from '../../../../models/coupons2.model';
import { getStudentCoupons, getStudentCouponTypes } from '../../../../api/coupons2.api';
import useNicePayModule, { ParsedResponse } from '../../../../hooks/useNicePayModule';
import CouponManagmentModal from './modals/CouponManagmentModal';
import PaymentProcessModal from './modals/PaymentProcessModal';
import useReceiptPrinterModule from '../../../../hooks/useReceiptPrinterModule';
import AdminPaymentConfirmModal from './modals/AdminPaymentConfirmModal';

export type IPaymentUnit = 'month' | 'semester';

const Box = styled.div<{
  width: string;
}>`
    width: ${({ width }) => width};
    display: inline-block;
`;

interface IProps {
  student?: IStudent;
  parent?: IParent;
  onSetPaymentMode: (mode: EPaymentMode) => void;
  paymentStep: EPaymentStep;
  setPaymentStep: (step: EPaymentStep) => void;
  studentPayments: IStudentPayment[];
  paymentRecords: IStudentPayment[];
  setPaymentRecords: (records: IStudentPayment[]) => void;
  paymentAmounts: IPaymentAmount[];
  setPaymentAmounts: (amounts: IPaymentAmount[]) => void;
  paymentResults: IPaymentResult[];
  setPaymentResults: (results: IPaymentResult[]) => void;
  amountTableResult?: IPaymentAmountTableResult;
  setAmountTableResult: (result: IPaymentAmountTableResult) => void;
  selectedCoupons: ISelectedCoupon[];
  setSelectedCoupons: (couponRecords: ISelectedCoupon[]) => void;
  firstPaymentReceiptUrl?: string;
  setFirstPaymentReceiptUrl: (url: string) => void;
  secondPaymentReceiptUrl?: string;
  setSecondPaymentReceiptUrl: (url: string) => void;
  thirdPaymentReceiptUrl?: string;
  setThirdPaymentReceiptUrl: (url: string) => void;
  doGetStudentPayments: () => void;
  sendLog: (message: string, object?: object) => void;
}

const PaymentProcessingMain: FC<IProps> = ({
                                             student,
                                             parent,
                                             onSetPaymentMode,
                                             paymentStep,
                                             setPaymentStep,
                                             studentPayments,
                                             paymentRecords,
                                             setPaymentRecords,
                                             paymentAmounts,
                                             setPaymentAmounts,
                                             paymentResults,
                                             setPaymentResults,
                                             amountTableResult,
                                             setAmountTableResult,
                                             selectedCoupons,
                                             setSelectedCoupons,
                                             firstPaymentReceiptUrl,
                                             setFirstPaymentReceiptUrl,
                                             secondPaymentReceiptUrl,
                                             setSecondPaymentReceiptUrl,
                                             thirdPaymentReceiptUrl,
                                             setThirdPaymentReceiptUrl,
                                             sendLog,
                                             doGetStudentPayments,
                                           }) => {
  const { t } = useTranslation();
  const { infoNotification, successNotification, errorNotification } = useNotification();
  const {
    refineFirstPaymentItems,
    refineSecondPaymentItems,
    refineThirdPaymentItems,
    dummyCompletedPaymentItems,
    handleTossResponse,
  } = usePayment();
  const { execute: niceExecute, error: niceError, isLoading: niceIsLoading, data: niceRes } = useNicePayModule();
  const { printReceipt } = useReceiptPrinterModule();
  const { data: tossRes, isLoading: tossIsLoading, error: tossError, execute: tossExecute } = useToss();
  const { data: res, isLoading, error, execute } = useAxios<IGetOrderInfo>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetStudentCouponTypes>();
  const { data: res4, isLoading: isLoading4, error: error4, execute: execute4 } = useAxios<IGetStudentCoupons>();
  const {
    data: submitRes, isLoading: submitIsLoading, error: submitError,
    execute: submitExecute,
  } = useAxios();
  const { execute: niceLogExecute } = useAxios();
  useLoader(
    isLoading || submitIsLoading,
    [error],
  );

  const [paymentInstallments, setPaymentInstallments] = useState<{ paymentNo: number; value: string }[]>([]);
  const [paymentUnit, setPaymentUnit] = useState<IPaymentUnit>('month');
  const [orderInfo, setOrderInfo] = useState<IOrderInfo[]>();
  const successOrderInfoRef = useRef<IOrderInfo[]>([]);
  const [amount, setAmount] = useState(0);
  const [awaitTossVisible, setAwaitTossVisible] = useState(false);
  const [issueCouponVisible, setIssueCouponVisible] = useState(false);
  const [adminPaymentVisible, setAdminPaymentVisible] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<{ paymentNo: number, status: 'RE' | 'SU' | 'FA' | 'PR' }[]>([]);
  const [prStatusPayments, setPrStatusPayments] = useState<number[]>([]); // PR 상태인 paymentNo들
  const [step, setStep] = useState<'START' | 'PLAYING' | 'COMPLETE'>('START');

  // 에러로그 발송
  useEffect(() => {
    if (niceRes && niceError && !niceIsLoading) {
      if(!niceRes.success) {
        console.log('niceRes', niceRes);
        errorNotification(niceError);
        niceLogExecute(postNicePaymentLog(niceRes));
      }
    }
  }, [niceRes, niceError, niceIsLoading]);

  useEffect(() => {
    if (student) {
      doGetStudentCoupons();
      execute3(getStudentCouponTypes(student.campusCode, student.studentCode));
    }
  }, [student]);

  useEffect(() => {
    if (!res4?.data?.length || !studentPayments.length) {
      return;
    }

    const newSelectedCoupons: ISelectedCoupon[] = [];
    const filteredCoupons = res4.data.filter(({
                                                useYn,
                                                expireDate,
                                              }) => useYn === 'N' && new Date() <= new Date(expireDate.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')));

    for (const coupon of filteredCoupons) {
      // 사용 가능한 payment 찾기 (이미 사용된 paymentNo는 제외)
      const availablePayment = studentPayments.find(payment =>
        payment.paymentTargetCode === coupon.paymentTargetCode &&
        payment.price >= newSelectedCoupons
          .filter(it => it.paymentNo === payment.paymentNo)
          .reduce((sum, it) => sum + it.amount, 0),
      );

      if (availablePayment) {
        newSelectedCoupons.push({
          ...coupon,
          paymentNo: availablePayment.paymentNo,
        });
      } else {
        newSelectedCoupons.push({
          ...coupon,
          paymentNo: 0,
        });
      }
    }

    setSelectedCoupons(newSelectedCoupons);
  }, [res4, studentPayments]);


  useEffect(() => {
    if (
      !student || !parent ||
      paymentStep === EPaymentStep.BeforePayment
    ) {
      return;
    }

    const updatePaymentBodies: IUpdatePaymentBody[] = [];

    paymentAmounts
      .forEach(paymentAmount => {
        // PR 상태인 결제건은 제외하고 체크된 것만 처리
        if (paymentAmount.checked && !prStatusPayments.includes(paymentAmount.paymentNo)) {
          const updatePaymentBody: IUpdatePaymentBody = {
            paymentNo: paymentAmount.paymentNo,
            amount: paymentAmount.amount,
            price: paymentAmount.price,
            paymentTargetCode: paymentAmount.paymentTargetCode,
            installmentPlanMonth: Number(getTossInstallment(paymentInstallments.find(it => it.paymentNo === paymentAmount.paymentNo)?.value || '일시불')),
            coupons: [],
          };
          const usedCoupons = selectedCoupons.filter(
            coupon => coupon.paymentNo === paymentAmount.paymentNo,
          );
          usedCoupons.forEach((coupon => {
            updatePaymentBody.coupons!.push({
              couponCode: coupon.couponStudentCode,
              amount: coupon.amount,
            });
          }));
          updatePaymentBodies.push(updatePaymentBody);
        }
      });


    if (updatePaymentBodies.length) {
      const updatePaymentReq: IUpdatePaymentLastBody = {
        parentMemberCode: parent.memberCode!,
        studentMemberCode: student.studentCode,
        campusCode: student.campusCode,
        paymentMtdCode: 'CD',
        paymentChannelCode: 'PO',
        payments: updatePaymentBodies,
      };

      if (
        paymentStep === EPaymentStep.FirstPayment ||
        paymentStep === EPaymentStep.SecondPayment ||
        paymentStep === EPaymentStep.ThirdPayment
      ) {
        execute(updatePaymentTodo(updatePaymentReq));
        sendLog(
          'updatePaymentTodo (getOrderInfo)',
          updatePaymentReq,
        );
      }

    } else if (paymentStep === EPaymentStep.FirstPayment) {
      setPaymentStep(EPaymentStep.PaymentCompleted);
    } else if (paymentStep === EPaymentStep.SecondPayment) {
      setPaymentStep(EPaymentStep.ThirdPayment);
    } else if (paymentStep === EPaymentStep.ThirdPayment) {
      setPaymentStep(EPaymentStep.PaymentCompleted);
    }
  }, [paymentStep]);

  useEffect(() => {
    const processPayment = async () => {
      if (
        res && !isLoading &&
        (paymentStep === EPaymentStep.FirstPayment ||
          paymentStep === EPaymentStep.SecondPayment ||
          paymentStep === EPaymentStep.ThirdPayment)
      ) {
        setStep('PLAYING');

        if (res.data.length > 0) {
          setPaymentStatus(res.data.map(it => ({ paymentNo: it.paymentNo, status: 'RE' })));

          for (const it of res.data) {
            // POS 결제 상태를 PR(처리중)로 insert
            try {
              await updatePaymentStatus(it.paymentNo, 'PR', 'POS 결제 시작');
              setPaymentStatus(prevStatus =>
                prevStatus.map(item =>
                  item.paymentNo === it.paymentNo ? { ...item, status: 'PR' } : item,
                ),
              );
            } catch (error) {
              console.error('상태 업데이트 실패:', error);
            }

            if (it.price > 0) {
              try {
                const catId = it.catId;
                const res = await niceExecute('approve', it.orderId, it.price, it.installmentPlanMonth, it.taxAmt, undefined, undefined, catId);
                console.log('success');

                await paymentSuccess(it, res);

                // POS 결제 성공 상태로 업데이트
                try {
                  await updatePaymentStatus(it.paymentNo, 'SU', 'POS 결제 완료');
                  setPaymentStatus(prevStatus =>
                    prevStatus.map(item =>
                      item.paymentNo === it.paymentNo ? { ...item, status: 'SU' } : item,
                    ),
                  );
                } catch (error) {
                  console.error('성공 상태 업데이트 실패:', error);
                }

                // await printReceipt(it.orderId);
              } catch (error) {
                console.error('결제 실패: ', error);

                // POS 결제 실패 상태로 업데이트
                try {
                  await updatePaymentStatus(it.paymentNo, 'FA', 'POS 결제 실패');
                  setPaymentStatus(prevStatus =>
                    prevStatus.map(item =>
                      item.paymentNo === it.paymentNo ? { ...item, status: 'FA' } : item,
                    ),
                  );
                } catch (updateError) {
                  console.error('실패 상태 업데이트 실패:', updateError);
                }

                paymentFailure(res.data);
                return;
              }
            } else {
              // 0원 결제 처리
              const req = {
                oid: it.orderId,
                tamt: amount,
              };

              submitExecute(createNicePaymentTodo(req));
              sendLog('createPaymentTodo (0)', req);

              // 0원 결제 완료 상태로 업데이트
              try {
                await updatePaymentStatus(it.paymentNo, 'SU', '0원 결제 완료');
                setPaymentStatus(prevStatus =>
                  prevStatus.map(item =>
                    item.paymentNo === it.paymentNo ? { ...item, status: 'SU' } : item,
                  ),
                );
              } catch (error) {
                console.error('0원 결제 상태 업데이트 실패:', error);
              }

              // ✅ useRef의 `current` 속성을 직접 업데이트
              successOrderInfoRef.current = [...successOrderInfoRef.current, it];
            }
          }

          paymentEnd();
        }
      }
    };

    processPayment(); // 비동기 함수 실행
  }, [res, isLoading]);


  const paymentEnd = () => {
    successNotification(t('student.student.message38'));
    setPaymentStep(EPaymentStep.PaymentCompleted);
    setStep('COMPLETE');
  };

  const paymentSuccess = async (orderInfo: IOrderInfo, res: ParsedResponse) => {
    const req = {
      ...res,
      oid: orderInfo.orderId,
      tamt: orderInfo.price,
    };

    await submitExecute(createNicePaymentTodo(req));
    sendLog('createNicePaymentTodo (success)', req);

    // ✅ useRef의 `current` 속성을 직접 업데이트
    successOrderInfoRef.current = [...successOrderInfoRef.current, orderInfo];

    setPaymentStatus(prevStatus =>
      prevStatus.map(item =>
        item.paymentNo === orderInfo.paymentNo ? { ...item, status: 'SU' } : item,
      ),
    );
  };

  const paymentFailure = async (orderInfos: IOrderInfo[]) => {
    const successOrderIds = new Set(successOrderInfoRef.current.map(order => String(order.orderId))); // ✅ 최신 성공 목록 가져오기

    console.log('✅ 성공한 주문 목록: ', successOrderInfoRef.current);
    console.log('✅ 성공한 주문 ID 목록: ', successOrderIds);

    const failedOrders = orderInfos.filter(order => !successOrderIds.has(String(order.orderId)));

    console.log('✅ 실패한 주문 목록: ', failedOrders);

    for (const orderInfo of failedOrders) {
      const req = {
        oid: orderInfo.orderId,
        tamt: orderInfo.price,
      };

      await submitExecute(createNicePaymentTodo(req));

      setPaymentStatus(prevStatus =>
        prevStatus.map(item =>
          item.paymentNo === orderInfo.paymentNo ? { ...item, status: 'FA' } : item,
        ),
      );
    }

    changePaymentResults(`실패 / 결제 오류`);
    errorNotification(t('student.student.message36'));
    setStep('COMPLETE');
    setPaymentStep(EPaymentStep.PaymentCompleted);
  };

  const doGetStudentCoupons = () => {
    if (student?.studentCode) {
      execute4(getStudentCoupons(student.studentCode));
    }
  };

  const onAdminSubmit = (paymentMtdCode: string, reasonCode: string) => {
    if (!student || !parent) return;

    /* 신입생인데 온라인 수강료만 결제하려고 한 경우 */
    if (student.statusCode === 'NR' &&
      (paymentAmounts.find(payment => payment.paymentTargetCode === 'ON' && payment.checked
        ||
        paymentAmounts.find(payment => payment.paymentTargetCode === 'AL' && payment.checked)
        ||
        paymentAmounts.find(payment => payment.paymentTargetCode === 'ST' && payment.checked),
      ))) {
      if (!paymentAmounts.find(payment => payment.paymentTargetCode === 'TU' && payment.checked)) {
        return infoNotification('미등록생은 수강료 선택 없이 결제할 수 없습니다.');
      }
    }

    const updatePaymentBodies: IUpdatePaymentBody[] = [];

    paymentAmounts
      .forEach(paymentAmount => {
        // PR 상태인 결제건은 제외하고 체크된 것만 처리
        if (paymentAmount.checked && !prStatusPayments.includes(paymentAmount.paymentNo)) {
          const updatePaymentBody: IUpdatePaymentBody = {
            paymentNo: paymentAmount.paymentNo,
            amount: paymentAmount.amount,
            price: paymentAmount.price,
            paymentTargetCode: paymentAmount.paymentTargetCode,
            installmentPlanMonth: Number(getTossInstallment(paymentInstallments.find(it => it.paymentNo === paymentAmount.paymentNo)?.value || '일시불')),
            coupons: [],
          };
          const usedCoupons = selectedCoupons.filter(
            coupon => coupon.paymentNo === paymentAmount.paymentNo,
          );
          usedCoupons.forEach((coupon => {
            updatePaymentBody.coupons!.push({
              couponCode: coupon.couponStudentCode,
              amount: coupon.amount,
            });
          }));
          updatePaymentBodies.push(updatePaymentBody);
        }
      });


    if (updatePaymentBodies.length) {
      const updatePaymentReq: IUpdatePaymentLastBody = {
        parentMemberCode: parent.memberCode!,
        studentMemberCode: student.studentCode,
        campusCode: student.campusCode,
        paymentMtdCode,
        reasonCode,
        paymentChannelCode: 'PO',
        payments: updatePaymentBodies,
      };

      execute(adminPaymentTodo(updatePaymentReq));
      sendLog(
        'updatePaymentTodo (getOrderInfo)',
        updatePaymentReq,
      );
    }

    setPaymentStatus(updatePaymentBodies.map(it => ({ paymentNo: it.paymentNo, status: 'SU' })));

    paymentEnd();
  };

  const onSubmit = () => {
    if (!student) {
      return;
    }

    /* 신입생인데 온라인 수강료만 결제하려고 한 경우 */
    if (student.statusCode === 'NR' &&
      (paymentAmounts.find(payment => payment.paymentTargetCode === 'ON' && payment.checked
        ||
        paymentAmounts.find(payment => payment.paymentTargetCode === 'AL' && payment.checked)
        ||
        paymentAmounts.find(payment => payment.paymentTargetCode === 'ST' && payment.checked),
      ))) {
      if (!paymentAmounts.find(payment => payment.paymentTargetCode === 'TU' && payment.checked)) {
        return infoNotification('미등록생은 수강료 선택 없이 결제할 수 없습니다.');
      }
    }

    setAwaitTossVisible(true);
    setStep('START');
    setPaymentStep(EPaymentStep.FirstPayment);
  };

  const changePaymentResults = (message: string) => {
    const index = paymentResults.findIndex(({ label }) => label === '상태');
    const newPaymentResults = [...paymentResults];

    if (index === -1) {
      return;
    }

    switch (paymentStep) {
      case EPaymentStep.FirstPayment:
        newPaymentResults[index] = {
          ...paymentResults[index],
          firstPaymentTuition: message,
        };
        break;
      case EPaymentStep.SecondPayment:
        newPaymentResults[index] = {
          ...paymentResults[index],
          secondPaymentTuition: message,
        };
        break;
      case EPaymentStep.ThirdPayment:
        newPaymentResults[index] = {
          ...paymentResults[index],
          thirdPaymentTuition: message,
        };
        break;
    }

    setPaymentResults(newPaymentResults);
  };

  const onConfirm = () => {
    doGetStudentPayments();
    onSetPaymentMode(EPaymentMode.PaymentToBeMade);
    setPaymentStep(EPaymentStep.BeforePayment);
  };

  return (
    <>
      <div className="comm-tab__line mt-2">
        <ul>
          <li className={`${paymentUnit === 'month' ? 'active' : ''}`}>
            <a href="#"
               onClick={() => setPaymentUnit('month')}
               className="btn-item">
              {t('student.student.option24')}
            </a>
          </li>

          <li className={`${paymentUnit === 'semester' ? 'active' : ''}`}>
            <a href="#"
               onClick={() => setPaymentUnit('semester')}
               className="btn-item">{t('student.student.option25')}
            </a>
          </li>
        </ul>
      </div>

      <div>
        <PaymentProcessingTable
          data={studentPayments}
          paymentUnit={paymentUnit}
          paymentRecords={paymentRecords}
          setPaymentRecords={setPaymentRecords}
          paymentAmounts={paymentAmounts}
          setPaymentAmounts={setPaymentAmounts}
          paymentResults={paymentResults}
          setPaymentResults={setPaymentResults}
          selectedCoupons={selectedCoupons}
          setSelectedCoupons={setSelectedCoupons}
          doGetStudentPayments={doGetStudentPayments}
          paymentInstallments={paymentInstallments}
          setPaymentInstallments={setPaymentInstallments}
          paymentStatus={paymentStatus}
          prStatusPayments={prStatusPayments}
          setPrStatusPayments={setPrStatusPayments}
        />

        <div className="comm-bx__between">
          <div className="comm-btn__area left">
            <Button
              className="btn-basic gray-line"
              onClick={onConfirm}
              text={t('common.button.back')}
            />
            <Button
              className="btn-basic pink"
              text={t('관리자 결제')}
              onClick={() => setAdminPaymentVisible(true)}
              disabled={paymentStep !== EPaymentStep.BeforePayment || paymentAmounts.every(it => !it.checked)}
            />
          </div>
          <div className="comm-btn__space comm-btn__area right">
            <Button
              className="btn-basic pink-line"
              text={'쿠폰 관리'}
              onClick={() => setIssueCouponVisible(true)}
              disabled={paymentStep !== EPaymentStep.BeforePayment}
            />
            <Button
              className="btn-basic pink"
              text={t('student.student.button18')}
              onClick={onSubmit}
              disabled={paymentStep !== EPaymentStep.BeforePayment || paymentAmounts.every(it => !it.checked)}
            />
            <Button
              className="btn-basic gray"
              text={t('common.button.cancel')}
              onClick={onConfirm}
              disabled={paymentStep !== EPaymentStep.BeforePayment}
            />
          </div>
        </div>
      </div>

      {awaitTossVisible && (
        <PaymentProcessModal
          visible={awaitTossVisible}
          onCancel={() => setAwaitTossVisible(false)}
          step={step}
          successCount={paymentStatus.filter(it => it.status === 'SU').length}
          failCount={paymentStatus.filter(it => it.status === 'FA').length}
          totalCount={paymentStatus.length}
        />
      )}

      {issueCouponVisible && (
        <CouponManagmentModal
          visible={issueCouponVisible}
          setVisible={setIssueCouponVisible}
          student={student}
          couponTypes={res3?.data}
          coupons={res4}
          doGetStudentCoupons={doGetStudentCoupons}
          sendLog={sendLog}
        />
      )}

      {adminPaymentVisible && (
        <AdminPaymentConfirmModal
          visible={adminPaymentVisible}
          onCancel={() => setAdminPaymentVisible(false)}
          onSubmit={onAdminSubmit}
        />
      )}
    </>
  );
};

export default PaymentProcessingMain;